import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DataGrid, GridRowModes, GridActionsCellItem, GridRowEditStopReasons, GridColDef } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import { apiUrl } from '../utils/api';
import { dataGridSx } from '../utils/dataGridStyles';
import {
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Chip,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';

interface BaseScopeCategoriesProps {
  projectId?: any;
  access?: string;
  deleteAllAccess?: string;
  userId?: any;
}

const BaseScopeCategories: React.FC<BaseScopeCategoriesProps> = ({ projectId = null, access = 'none', deleteAllAccess = 'none', userId }) => {
  const readOnly = access !== 'write';
  const canDeleteAll = deleteAllAccess === 'write';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});
  const [newCategory, setNewCategory] = useState({ code: '', name: '', sort_order: '', is_active: true });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [importSourceOpen, setImportSourceOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mapFile, setMapFile] = useState<File | null>(null);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const CATEGORY_FIELDS = ['Code', 'Name', 'Sort Order', 'Active'];
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [excelImporting, setExcelImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummaryData, setImportSummaryData] = useState<any>(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<any>(null);

  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const fetchCategories = useCallback(async () => {
    if (access === 'none' || !projectId) {
      setCategories([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(apiUrl(`/api/budget-categories?project_id=${projectId}`));
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load BOQ categories');
      }
      const data = await response.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading BOQ categories:', error);
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [access, projectId, showNotification]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (!excelImporting) return;
    const timer = setInterval(() => {
      setImportProgress(p => (p < 90 ? p + 5 : p));
    }, 300);
    return () => clearInterval(timer);
  }, [excelImporting]);

  const handleRowModesModelChange = useCallback((newModel) => {
    setRowModesModel(newModel);
  }, []);

  const handleRowEditStop = useCallback((params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      setRowModesModel((prev) => ({ ...prev, [params.id]: { mode: GridRowModes.View } }));
    }
  }, []);

  const handleAdd = useCallback(async () => {
    if (readOnly || !projectId || addLoading) return;
    if (!newCategory.name.trim()) {
      showNotification('Name is required', 'error');
      return;
    }

    const payload = {
      project_id: projectId,
      code: newCategory.code.trim() || null,
      name: newCategory.name.trim(),
      sort_order: newCategory.sort_order === '' ? null : Number(newCategory.sort_order),
      is_active: Boolean(newCategory.is_active)
    };

    setAddLoading(true);
    try {
      const response = await fetch(apiUrl('/api/budget-categories'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': String(userId) } : {})
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create category');
      }

      setNewCategory({ code: '', name: '', sort_order: '', is_active: true });
      fetchCategories();
      showNotification('Category created successfully');
    } catch (error) {
      console.error('Error creating category:', error);
      showNotification(error.message, 'error');
    } finally {
      setAddLoading(false);
    }
  }, [readOnly, projectId, newCategory, fetchCategories, showNotification, addLoading]);

  const handleDeleteClick = useCallback((id) => () => {
    if (readOnly) return;
    const category = categories.find(c => c.category_id === id);
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  }, [readOnly, categories]);

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await fetch(apiUrl(`/api/budget-categories/${categoryToDelete.category_id}`), {
        method: 'DELETE',
        headers: {
          ...(userId ? { 'X-User-ID': String(userId) } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete category');
      }

      fetchCategories();
      showNotification('Category deleted successfully');
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error('Error deleting category:', error);
      showNotification(error.message, 'error');
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleEditClick = useCallback((id) => () => {
    if (readOnly) return;
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, [readOnly]);

  const handleSaveClick = useCallback((id) => () => {
    if (readOnly) return;
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  }, [readOnly]);

  const handleCancelClick = useCallback((id) => () => {
    if (readOnly) return;
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));
  }, [readOnly]);

  const processRowUpdate = useCallback(async (updatedRow) => {
    const { category_id, code, name, sort_order, is_active } = updatedRow;

    if (!name || !name.trim()) {
      showNotification('Name is required', 'error');
      throw new Error('Name is required');
    }

    const payload = {
      code: code?.trim() || null,
      name: name.trim(),
      sort_order: sort_order === '' || sort_order === null ? null : Number(sort_order),
      is_active: Boolean(is_active)
    };

    try {
      const response = await fetch(apiUrl(`/api/budget-categories/${category_id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': String(userId) } : {})
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to update category');
      }

      fetchCategories();
      showNotification('Category updated successfully');
      return { ...updatedRow, ...responseData };
    } catch (error) {
      console.error('Error updating category:', error);
      showNotification(error.message, 'error');
      throw error;
    }
  }, [fetchCategories, showNotification]);

  const handleProcessRowUpdateError = useCallback((error) => {
    if (error?.message) {
      showNotification(error.message, 'error');
    }
  }, [showNotification]);

  const handleRowDoubleClick = useCallback((params) => {
    if (readOnly) return;
    setRowModesModel((prev) => ({ ...prev, [params.id]: { mode: GridRowModes.Edit } }));
  }, [readOnly]);

  // Export template
  const handleDownloadTemplate = () => {
    fetch(apiUrl(`/api/budget-categories/template`))
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'boq_categories_template.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => showNotification('Failed to download template', 'error'));
  };

  // Export to Excel
  const handleExport = () => {
    const rows = categories.map(cat => ({
      Code: cat.code || '',
      Name: cat.name || '',
      'Sort Order': cat.sort_order || '',
      Active: cat.is_active ? 'Yes' : 'No'
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BOQ Categories');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'boq_categories.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Import from Excel
  const handleImportClick = () => {
    if (readOnly) return;
    setImportSourceOpen(true);
  };

  const handleImportSource = (isThird: boolean) => {
    setImportSourceOpen(false);
    if (isThird) {
      setMapFile(null);
      setFileHeaders([]);
      setMapping({});
      setMappingOpen(true);
    } else { fileInputRef.current?.click(); }
  };

  const handleMapFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMapFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
      const headers = ((rows as any)[0] || []).map((v: any) => String(v || ''));
      setFileHeaders(headers);
      const auto: Record<string, string> = { ...mapping };
      CATEGORY_FIELDS.forEach(fld => {
        const idx = headers.findIndex(h => h.toLowerCase().replace(/\s+/g, '_') === fld.toLowerCase().replace(/\s+/g, '_'));
        if (idx >= 0) auto[fld] = headers[idx];
      });
      setMapping(auto);
    } catch { setFileHeaders([]); }
  };

  const handleMappingSubmit = async () => {
    if (!mapFile) { alert('Select a file'); return; }
    try {
      const buf = await mapFile.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' }) as any[];
      const norm = rows.map(r => CATEGORY_FIELDS.reduce((obj, fld) => ({ ...obj, [fld]: (r as any)[mapping[fld]] ?? '' }), {}));
      const newWb = XLSX.utils.book_new();
      const newWs = XLSX.utils.json_to_sheet(norm);
      XLSX.utils.book_append_sheet(newWb, newWs, 'Sheet1');
      const buf2 = XLSX.write(newWb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf2], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const mappedFile = new File([blob], 'mapped.xlsx');
      setMappingOpen(false);
      await runImport(mappedFile);
    } catch (err: any) { alert(err.message || 'Mapping import failed'); }
  };

  const runImport = async (file: File) => {
    try {
      setExcelImporting(true);
      setImportProgress(0);
      const fd = new FormData();
      fd.append('file', file);
      const response = await fetch(apiUrl(`/api/budget-categories/import?confirm=true&project_id=${projectId}&overwrite=true`), {
        method: 'POST',
        headers: { 'X-User-ID': String(userId) },
        body: fd
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to import categories');
      const added = data.created || 0;
      const overwritten = data.updated || 0;
      const skipped = data.skipped || 0;
      const failed = (data.errors || []).length;
      const logRows = [
        ...Array(added).fill(null).map((_: any, i: number) => ({ label: `Category ${i + 1}`, status: 'Added' })),
        ...Array(overwritten).fill(null).map((_: any, i: number) => ({ label: `Category ${added + i + 1}`, status: 'Overwritten' })),
        ...Array(skipped).fill(null).map((_: any, i: number) => ({ label: `Existing Category ${i + 1}`, status: 'Skipped' })),
        ...(data.errors || []).map((e: any) => ({ label: `Row ${e.row || '?'}`, status: `Failed: ${e.field ? e.field + ' - ' : ''}${e.error || e}` }))
      ];
      setImportProgress(100);
      setImportSummaryData({ total: added + overwritten + skipped + failed, added, overwritten, skipped, failed, rows: logRows });
      setImportSummaryOpen(true);
      fetchCategories();
    } catch (err: any) {
      setImportProgress(0);
      setImportSummaryData({ total: 0, added: 0, overwritten: 0, skipped: 0, failed: 1, error: err.message || 'Import failed', rows: [{ label: 'Import error', status: `Failed: ${err.message || 'Import failed'}` }] });
      setImportSummaryOpen(true);
      showNotification(err.message || 'Import failed', 'error');
    } finally { setExcelImporting(false); }
  };

  const downloadImportLog = (data: any) => {
    const logRows = (data.rows || []).map((r: any, i: number) => {
      const s = r.status || ''
      let status: string, notes: string
      if (s === 'Added') { status = 'Pass'; notes = 'New Item added' }
      else if (s === 'Overwritten') { status = 'Pass'; notes = 'Item overwritten' }
      else if (s === 'Skipped') { status = 'Pass'; notes = 'Existing kept (skipped)' }
      else { status = 'Failed'; notes = s.replace(/^Failed:\s*/i, '') }
      return { row_no: i + 2, category: r.label, status, notes }
    })
    fetch(apiUrl('/api/base-scope-categories/import-log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: logRows })
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = 'BOQCategory_Import_Log.xlsx'; a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to generate import log'))
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    runImport(file);
  };

  // Delete all
  const handleDeleteAllClick = () => {
    if (!canDeleteAll) return;
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = () => {
    fetch(apiUrl(`/api/budget-categories/delete-all?project_id=${projectId}`), {
      method: 'DELETE',
      headers: { 'X-User-ID': String(userId) }
    })
      .then(res => {
        if (!res.ok) throw new Error('Delete all failed');
        fetchCategories();
        showNotification('All BOQ categories deleted successfully', 'success');
        setDeleteAllDialogOpen(false);
      })
      .catch(err => {
        showNotification(err.message, 'error');
        setDeleteAllDialogOpen(false);
      });
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'category_id',
      headerName: 'ID',
      width: 90,
      type: 'number'
    },
    {
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 120,
      editable: !readOnly
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 200,
      editable: !readOnly
    },
    {
      field: 'sort_order',
      headerName: 'Sort Order',
      width: 140,
      type: 'number',
      editable: !readOnly,
      valueFormatter: (value: any) => {
        return value === null || value === undefined || value === '' ? '' : String(value);
      }
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 140,
      type: 'boolean',
      editable: !readOnly,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      valueGetter: (value: any) => {
        if (!value) return null;
        try {
          return new Date(value);
        } catch (error) {
          return null;
        }
      }
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      width: 160
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: ({ id }) => {
        if (readOnly) return [];

        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isEditing) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
              label="Save"
              onClick={handleSaveClick(id)}
              showInMenu={false}
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
              label="Cancel"
              onClick={handleCancelClick(id)}
              showInMenu={false}
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
            label="Edit"
            onClick={handleEditClick(id)}
            showInMenu={false}
          />,
          <GridActionsCellItem
            icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
            label="Delete"
            onClick={handleDeleteClick(id)}
            showInMenu={false}
          />
        ];
      }
    }
  ], [readOnly, rowModesModel, handleSaveClick, handleCancelClick, handleEditClick, handleDeleteClick]);

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You don't have permission to access this page.</Alert>
      </Box>
    );
  }

  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a project to manage BOQ categories.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>

      {!readOnly && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Add Category
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Code"
                  value={newCategory.code}
                  onChange={(e) => setNewCategory((prev) => ({ ...prev, code: e.target.value }))}
                  placeholder="Optional"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Name"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Electrical"
                  size="small"
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Sort Order"
                  value={newCategory.sort_order}
                  onChange={(e) => setNewCategory((prev) => ({ ...prev, sort_order: e.target.value }))}
                  placeholder="Optional"
                  size="small"
                  type="number"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 1 }}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={newCategory.is_active}
                      onChange={(e) => setNewCategory((prev) => ({ ...prev, is_active: e.target.checked }))}
                      size="small"
                    />
                  )}
                  label="Active"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAdd}
                  disabled={!newCategory.name.trim() || addLoading}
                  startIcon={addLoading ? <CircularProgress size={20} /> : <AddIcon />}
                >
                  {addLoading ? 'Adding...' : 'Add'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={categories}
            columns={columns}
            getRowId={(row) => row.category_id}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            onRowDoubleClick={handleRowDoubleClick}
            loading={loading}
            autoHeight
            columnVisibilityModel={{
              category_id: false,
              created_at: false,
              created_by: false
            }}
            initialState={{
              sorting: { sortModel: [{ field: 'sort_order', sort: 'asc' }] },
              pagination: { paginationModel: { page: 0, pageSize: 25 } }
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              ...dataGridSx,
              '& .MuiSelect-select': { color: 'text.primary' },
              '& .MuiInputBase-input': { color: 'text.primary' }
            }}
          />
        </CardContent>
      </Card>

      {/* Import/Export Buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handleDownloadTemplate}>
          Export Template
        </Button>
        <Button variant="contained" onClick={handleExport}>
          Export To Excel
        </Button>
        <Button variant="contained" onClick={handleImportClick} disabled={readOnly}>
          Import From Excel
        </Button>
        {canDeleteAll && (
          <Button variant="contained" color="error" onClick={handleDeleteAllClick}>
            Delete All
          </Button>
        )}
        <input
          type="file"
          accept=".xlsx"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Import Source Dialog */}
      <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
        <DialogTitle>Import From Excel</DialogTitle>
        <DialogContent>Choose source type:</DialogContent>
        <DialogActions>
          <Button onClick={() => handleImportSource(false)}>Template</Button>
          <Button onClick={() => handleImportSource(true)}>Third-party (Map Columns)</Button>
          <Button onClick={() => setImportSourceOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Column Mapping Dialog */}
      <Dialog open={mappingOpen} onClose={() => { setMappingOpen(false); setFileHeaders([]); setMapFile(null); setMapping({}); }} maxWidth="md" fullWidth>
        <DialogTitle>Map Columns (Third-party Excel)</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 8 }}>
            <input type="file" accept=".xlsx,.xls" onChange={handleMapFileChange} />
          </div>
          {!!fileHeaders.length && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {CATEGORY_FIELDS.map(f => (
                <div key={f}>
                  <div style={{ fontSize: 12, color: '#555' }}>{f}</div>
                  <TextField select size="small" fullWidth value={mapping[f] || ''} onChange={(e) => setMapping(prev => ({ ...prev, [f]: e.target.value }))}>
                    <MenuItem value="">(none)</MenuItem>
                    {fileHeaders.map(h => <MenuItem key={`${f}-${h}`} value={h}>{h}</MenuItem>)}
                  </TextField>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
          <Button onClick={handleMappingSubmit} disabled={!mapFile}>Import</Button>
        </DialogActions>
      </Dialog>

      {/* Importing Progress Dialog */}
      <Dialog open={excelImporting}>
        <DialogTitle>Importing...</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <LinearProgress variant="determinate" value={importProgress} />
          <Typography sx={{ mt: 1 }} align="center">{importProgress}%</Typography>
        </DialogContent>
      </Dialog>

      {/* Import Summary Dialog */}
      <Dialog open={importSummaryOpen} onClose={() => setImportSummaryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{importSummaryData?.error ? 'Import Failed' : 'Import Summary'}</DialogTitle>
        <DialogContent>
          {importSummaryData && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>Total categories processed: <strong>{importSummaryData.total}</strong></Typography>
              <Typography variant="body2" sx={{ color: 'green' }}>Added: {importSummaryData.added}</Typography>
              <Typography variant="body2" sx={{ color: 'goldenrod' }}>Overwritten: {importSummaryData.overwritten}</Typography>
              <Typography variant="body2" sx={{ color: 'grey' }}>Skipped: {importSummaryData.skipped}</Typography>
              <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>Failed: {importSummaryData.failed}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total changes: {importSummaryData.added + importSummaryData.overwritten}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => downloadImportLog(importSummaryData)} variant="outlined">Download Import Log</Button>
          <Button onClick={() => setImportSummaryOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete BOQ Category
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the following category?
          </Typography>
          {categoryToDelete && (
            <Box sx={{
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 2,
              border: 1,
              borderColor: 'grey.300'
            }}>
              {categoryToDelete.code && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Code
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5 }}>
                    {categoryToDelete.code}
                  </Typography>
                </>
              )}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Name
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {categoryToDelete.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleDeleteAllCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete All BOQ Categories
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>ALL</strong> BOQ categories for this project?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will permanently remove all categories from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleDeleteAllCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity as AlertColor}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BaseScopeCategories;
